import { AlertTriangle, History, Loader2 } from "lucide-react";
import {
  AnalyticsAccountIdentity,
  formatPlatform,
} from "./AnalyticsAccountIdentity";
import { AnalyticsBackfillGate } from "./AnalyticsBackfillGate";
import {
  AnalyticsDiscoveryConnection,
  AnalyticsSetupConnection,
} from "../models/AnalyticsOverview";
import { Button } from "./ui/button";

export function AnalyticsDiscoveryNotice({
  connections,
  preparing = false,
  backfilling = false,
  showProgress = true,
  connectingConnectionId,
  onReconnect,
  error,
  onRetry,
}: {
  connections: AnalyticsDiscoveryConnection[];
  preparing?: boolean;
  backfilling?: boolean;
  showProgress?: boolean;
  connectingConnectionId: string | null;
  onReconnect: (connection: AnalyticsSetupConnection) => void;
  error: Error | null;
  onRetry: () => void;
}) {
  // An account that finished an import before is on its daily refresh. Its
  // posts are already in the dashboard, so the refresh stays silent.
  const firstImports = connections.filter(
    (connection) => connection.completedAt === null,
  );
  const importing = firstImports.filter((connection) =>
    ["queued", "running"].includes(connection.status),
  );
  const waiting = firstImports.filter(
    (connection) => connection.status === "retrying",
  );
  const reconnect = connections.filter(
    (connection) => connection.status === "reconnect_required",
  );
  if (
    !backfilling &&
    !importing.length &&
    !waiting.length &&
    !reconnect.length &&
    !error
  )
    return null;
  // The full setup card is for a workspace with nothing to show yet. Once any
  // account has imported, a newly importing account is a row in the notice
  // instead of a second loading screen above the dashboard.
  const hasImported = connections.some(
    (connection) => connection.completedAt !== null,
  );
  const importingRows = hasImported && !backfilling ? importing : [];
  const showImportProgress =
    showProgress &&
    !importingRows.length &&
    (importing.length > 0 || backfilling);
  const showNotice =
    waiting.length > 0 ||
    reconnect.length > 0 ||
    importingRows.length > 0 ||
    (error && showProgress && !showImportProgress);
  // An import that needs nothing from the person is news, not a warning.
  const calm = !reconnect.length && !waiting.length && !error;
  const tone = calm
    ? {
        frame: "border-border bg-background",
        badge: "bg-muted text-muted-foreground",
        title: "text-foreground",
        body: "text-muted-foreground",
        rule: "border-border",
        divide: "divide-border",
        spinner: "text-muted-foreground",
      }
    : {
        frame: "border-amber-200 bg-[oklch(0.98_0.018_75)]",
        badge: "bg-amber-100 text-amber-800",
        title: "text-amber-950",
        body: "text-amber-900",
        rule: "border-amber-200",
        divide: "divide-amber-200",
        spinner: "text-amber-700",
      };
  return (
    <>
      {showImportProgress && !showNotice && (
        <AnalyticsBackfillGate
          importing={importing.length > 0}
          preparing={preparing}
          error={error}
          onRetry={onRetry}
        />
      )}
      {showNotice && (
        <aside
          aria-label="Post import coverage"
          className={`mt-6 overflow-hidden rounded-xl border ${tone.frame}`}
        >
          <div className="flex gap-3 px-4 py-4 sm:px-5">
            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone.badge}`}>
              {calm ? (
                <History className="h-4 w-4" aria-hidden />
              ) : (
                <AlertTriangle className="h-4 w-4" aria-hidden />
              )}
            </div>
            <div className="min-w-0">
              <h2 className={`text-sm font-semibold ${tone.title}`}>
                {reconnect.length > 0
                  ? "See all your posts in one dashboard"
                  : error
                    ? "Post import couldn’t start"
                    : waiting.length > 0
                      ? "Post import needs more time"
                      : "Importing your past posts"}
              </h2>
              <p className={`mt-1 max-w-3xl text-sm leading-5 ${tone.body}`}>
                {reconnect.length > 0
                  ? "Reconnect the accounts below to track all posts in one dashboard, including those not published through Wavegen."
                  : error
                    ? "Try again to start importing your posts."
                    : "No action needed. You can check back later."}
              </p>
            </div>
          </div>
          {showImportProgress && (
            <div
              role="status"
              className={`flex items-start gap-3 border-t bg-background px-4 py-4 sm:px-5 ${tone.rule}`}
            >
              <Loader2
                className={`mt-0.5 h-4 w-4 shrink-0 animate-spin ${tone.spinner}`}
                aria-hidden
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {preparing
                    ? "Getting started…"
                    : importing.length > 0
                      ? "Importing posts from the last 180 days"
                      : "Adding data from your past posts"}
                </p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  You can leave this page and check back later.
                </p>
              </div>
            </div>
          )}
          <div className={`divide-y border-t bg-background ${tone.divide} ${tone.rule}`}>
            {reconnect.map((connection) => {
              const isConnecting =
                connectingConnectionId === connection.connectionId;
              return (
                <div
                  key={connection.connectionId}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                >
                  <AnalyticsAccountIdentity
                    profileName={connection.profileName}
                    platform={connection.platform}
                    detail="Reconnect to see all posts"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-10 w-full shrink-0 bg-background sm:w-auto"
                    disabled={!!connectingConnectionId}
                    onClick={() =>
                      onReconnect({
                        ...connection,
                        reason: "analytics_permission",
                      })
                    }
                  >
                    {isConnecting && (
                      <Loader2
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-hidden
                      />
                    )}
                    {isConnecting
                      ? "Opening…"
                      : `Reconnect ${formatPlatform(connection.platform)}`}
                  </Button>
                </div>
              );
            })}
            {importingRows.map((connection) => (
              <div
                key={connection.connectionId}
                className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5"
                role="status"
              >
                <AnalyticsAccountIdentity
                  profileName={connection.profileName}
                  platform={connection.platform}
                  detail="Importing posts from the last 180 days"
                />
                <Loader2
                  className={`h-4 w-4 shrink-0 animate-spin ${tone.spinner}`}
                  aria-hidden
                />
              </div>
            ))}
            {waiting.map((connection) => (
              <div
                key={connection.connectionId}
                className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5"
                role="status"
              >
                <AnalyticsAccountIdentity
                  profileName={connection.profileName}
                  platform={connection.platform}
                  detail="Import delayed. We’ll retry automatically."
                />
                <Loader2
                  className={`h-4 w-4 shrink-0 animate-spin ${tone.spinner}`}
                  aria-hidden
                />
              </div>
            ))}
          </div>
          {error && showProgress && (
            <div
              role="alert"
              className="flex flex-col gap-3 border-t border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
            >
              <p className="text-sm text-red-900">
                Post import couldn’t start. Please try again.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 bg-background"
                onClick={onRetry}
              >
                Retry import
              </Button>
            </div>
          )}
        </aside>
      )}
    </>
  );
}
